// ─────────────────────────────────────────
// GroupPage.js — individual group view
// ─────────────────────────────────────────
// Three tabs:
//   💬 Chat    — real-time group messenger
//   🏆 Compete — live scoring competitions
//   👥 Members — member list + join/leave

import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import GroupCompete from '../components/GroupCompete'
import GroupTournament from '../components/GroupTournament'
import GroupChat from '../components/GroupChat'
import AvatarImg from '../components/AvatarImg'
import Nav from '../components/Nav'
import { useIsMobile } from '../hooks/useIsMobile'

export default function GroupPage({ session }) {
  const isMobile = useIsMobile()
  const { groupId } = useParams()
  const [searchParams] = useSearchParams()

  const [group, setGroup] = useState(null)
  const [members, setMembers] = useState([])
  const [myMembership, setMyMembership] = useState(null)
  const [myProfile, setMyProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [joining, setJoining] = useState(false)
  const [joinError, setJoinError] = useState(null)
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'chat')

  // ── Edit group state ────────────────────────────────
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [editPrivacy, setEditPrivacy] = useState('public')
  const [editSaving, setEditSaving] = useState(false)
  const [editSaved, setEditSaved] = useState(false)
  const [memberSearch, setMemberSearch] = useState('')
  const [memberSearchResults, setMemberSearchResults] = useState([])
  const [memberSearching, setMemberSearching] = useState(false)
  const [removingId, setRemovingId] = useState(null)
  const [removeError, setRemoveError] = useState(null)
  const [addingId, setAddingId] = useState(null)
  const [addMemberError, setAddMemberError] = useState(null)
  const [addMemberSuccess, setAddMemberSuccess] = useState(null)

  useEffect(() => {
    fetchGroup()
    fetchMyProfile()
  }, [groupId])

  async function fetchMyProfile() {
    const { data } = await supabase
      .from('profiles')
      .select('username, avatar_emoji, avatar_url')
      .eq('id', session.user.id)
      .single()
    if (data) setMyProfile(data)
  }

  async function fetchGroup(isRetry = false) {
    // Fetch group details
    const { data: groupData } = await supabase
      .from('groups')
      .select('*')
      .eq('id', groupId)
      .single()

    // Fetch member rows (no profiles join — FK goes to auth.users not profiles)
    const { data: memberRows } = await supabase
      .from('group_members')
      .select('id, group_id, user_id, role, joined_at')
      .eq('group_id', groupId)
      .order('joined_at', { ascending: true })

    const rows = memberRows || []

    // Fetch profiles for all members in one separate query
    let membersWithProfiles = rows
    if (rows.length > 0) {
      const userIds = rows.map(m => m.user_id)
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id, username, avatar_emoji, avatar_url, handicap, email')
        .in('id', userIds)

      const profileMap = {}
      ;(profileData || []).forEach(p => { profileMap[p.id] = p })
      membersWithProfiles = rows.map(m => {
        const profile = profileMap[m.user_id] || null
        // If profile exists but no username, derive one from email
        if (profile && !profile.username && profile.email) {
          profile.username = profile.email.split('@')[0]
        }
        return { ...m, profiles: profile }
      })
    }

    // Auto-heal: if the group creator isn't in group_members yet (old groups),
    // insert them as admin and re-fetch once.
    const mine = membersWithProfiles.find(m => m.user_id === session.user.id)
    if (!mine && !isRetry && groupData?.created_by === session.user.id) {
      const { error } = await supabase
        .from('group_members')
        .insert({ group_id: groupId, user_id: session.user.id, role: 'admin' })
      if (!error) {
        fetchGroup(true) // re-fetch with the new row in place
        return
      }
    }

    setGroup(groupData)
    setMembers(membersWithProfiles)
    setMyMembership(mine || null)
    if (groupData) {
      setEditName(groupData.name || '')
      setEditDesc(groupData.description || '')
      setEditPrivacy(groupData.privacy || 'public')
    }
    setLoading(false)
  }

  async function handleJoin() {
    setJoining(true)
    setJoinError(null)

    // Insert the membership row
    const { error } = await supabase
      .from('group_members')
      .insert({ group_id: groupId, user_id: session.user.id, role: 'member' })

    if (error) {
      setJoinError('Could not join group. Please try again.')
      setJoining(false)
      return
    }

    // Fetch the user's profile separately to build the member row for state
    const { data: profile } = await supabase
      .from('profiles')
      .select('id, username, avatar_emoji, avatar_url, handicap')
      .eq('id', session.user.id)
      .single()

    const newMember = {
      group_id: groupId,
      user_id: session.user.id,
      role: 'member',
      joined_at: new Date().toISOString(),
      profiles: profile || null
    }

    const newMembers = [...members, newMember]
    setMembers(newMembers)
    setMyMembership(newMember)

    await supabase
      .from('groups')
      .update({ member_count: newMembers.length })
      .eq('id', groupId)
    setGroup(prev => ({ ...prev, member_count: newMembers.length }))

    setJoining(false)
  }

  // ── Edit: save group settings ──────────────────────────────
  async function handleSaveSettings() {
    if (!editName.trim()) return
    setEditSaving(true)
    await supabase
      .from('groups')
      .update({ name: editName.trim(), description: editDesc.trim(), privacy: editPrivacy })
      .eq('id', groupId)
    setGroup(prev => ({ ...prev, name: editName.trim(), description: editDesc.trim(), privacy: editPrivacy }))
    setEditSaving(false)
    setEditSaved(true)
    setTimeout(() => setEditSaved(false), 2000)
  }

  // ── Edit: remove member ────────────────────────────────────
  async function handleRemoveMember(userId) {
    if (!window.confirm('Remove this member from the group?')) return
    setRemovingId(userId)
    setRemoveError(null)

    const { error } = await supabase
      .from('group_members')
      .delete()
      .eq('group_id', groupId)
      .eq('user_id', userId)

    if (error) {
      setRemoveError('Could not remove member — check Supabase RLS policies.')
      setRemovingId(null)
      return
    }

    // Only update UI after DB confirms the delete succeeded
    const newMembers = members.filter(m => m.user_id !== userId)
    setMembers(newMembers)
    await supabase.from('groups').update({ member_count: Math.max(0, newMembers.length) }).eq('id', groupId)
    setGroup(prev => ({ ...prev, member_count: Math.max(0, newMembers.length) }))
    setRemovingId(null)
  }

  // ── Edit: search users to add ──────────────────────────────
  useEffect(() => {
    if (!memberSearch.trim()) { setMemberSearchResults([]); return }
    const timer = setTimeout(async () => {
      setMemberSearching(true)
      const q = memberSearch.trim()
      // Search username AND full_name so admins can find people either way
      const [{ data: byUsername }, { data: byName }] = await Promise.all([
        supabase.from('profiles').select('id, username, full_name, avatar_emoji, avatar_url').ilike('username', `%${q}%`).limit(8),
        supabase.from('profiles').select('id, username, full_name, avatar_emoji, avatar_url').ilike('full_name', `%${q}%`).limit(8)
      ])
      const merged = [...(byUsername || []), ...(byName || [])]
      const seen = new Set()
      const deduped = merged.filter(p => { if (seen.has(p.id)) return false; seen.add(p.id); return true })
      // Filter out people already in the group
      const existingIds = new Set(members.map(m => m.user_id))
      setMemberSearchResults(deduped.filter(p => !existingIds.has(p.id)))
      setMemberSearching(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [memberSearch, members])

  // ── Edit: add member ───────────────────────────────────────
  async function handleAddMember(profile) {
    setAddingId(profile.id)
    setAddMemberError(null)

    const { error } = await supabase
      .from('group_members')
      .insert({ group_id: groupId, user_id: profile.id, role: 'member' })

    if (error) {
      setAddMemberError(
        error.code === '23505'
          ? `${profile.username || 'This user'} is already a member.`
          : `Could not add member: ${error.message}`
      )
    } else {
      const newMember = {
        group_id: groupId, user_id: profile.id, role: 'member',
        joined_at: new Date().toISOString(), profiles: profile
      }
      const newMembers = [...members, newMember]
      setMembers(newMembers)
      await supabase.from('groups').update({ member_count: newMembers.length }).eq('id', groupId)
      setGroup(prev => ({ ...prev, member_count: newMembers.length }))
      setMemberSearch('')
      setMemberSearchResults([])
      setAddMemberSuccess(`@${profile.username || profile.full_name || 'Member'} added!`)
      setTimeout(() => setAddMemberSuccess(null), 3000)
    }
    setAddingId(null)
  }

  async function handleLeave() {
    if (!window.confirm('Leave this group?')) return
    await supabase
      .from('group_members')
      .delete()
      .eq('group_id', groupId)
      .eq('user_id', session.user.id)

    const newMembers = members.filter(m => m.user_id !== session.user.id)
    setMembers(newMembers)
    setMyMembership(null)
    await supabase
      .from('groups')
      .update({ member_count: newMembers.length })
      .eq('id', groupId)
    setGroup(prev => ({ ...prev, member_count: newMembers.length }))
  }

  // ── RENDER ───────────────────────────────────────────

  if (loading) {
    return (
      <div style={styles.loadingScreen}>
        <div style={{ fontSize: 40 }}>⛳</div>
        <div style={{ color: '#6aad80', fontSize: 14, marginTop: 12 }}>Loading group...</div>
      </div>
    )
  }

  if (!group) {
    return (
      <div style={styles.loadingScreen}>
        <div style={{ fontSize: 40 }}>❓</div>
        <div style={{ color: 'white', fontSize: 16, marginTop: 12 }}>Group not found</div>
        <Link to="/groups" style={{ color: '#c8f135', marginTop: 16, fontSize: 14 }}>← Back to Groups</Link>
      </div>
    )
  }

  const isAdmin = myMembership?.role === 'admin'
  const isMember = !!myMembership
  const canJoin = group.privacy === 'public' && !isMember
  const isPrivateGate = group.privacy === 'private' && !isMember

  const adminMembers = members.filter(m => m.role === 'admin')
  const regularMembers = members.filter(m => m.role !== 'admin')

  return (
    <div style={{ ...styles.page, paddingBottom: isMobile ? 72 : 0 }}>

      {/* ── TOP BAR ── */}
      <div style={styles.topbar}>
        <div style={styles.logo}>BREAKIE</div>
        <div style={styles.topbarRight}>
          <span style={styles.topbarIcon}>{group.icon_emoji || '⛳'}</span>
          <span style={styles.topbarTitle}>{group.name}</span>
        </div>
      </div>

      <Nav />

      {/* ── GROUP HEADER ── */}
      <div style={styles.groupHeader}>
        <div style={styles.headerStats}>
          <div style={styles.headerStat}>
            <div style={styles.headerStatNum}>{members.length || group.member_count || 0}</div>
            <div style={styles.headerStatLabel}>Members</div>
          </div>
          <div style={styles.headerStatDivider} />
          <div style={styles.headerStat}>
            <div style={styles.headerStatNum}>{group.privacy === 'public' ? '🌐' : '🔒'}</div>
            <div style={styles.headerStatLabel}>{group.privacy === 'public' ? 'Public' : 'Private'}</div>
          </div>
          {isMember && (
            <>
              <div style={styles.headerStatDivider} />
              <div style={styles.headerStat}>
                <div style={styles.headerStatNum}>✓</div>
                <div style={styles.headerStatLabel}>{isAdmin ? 'Admin' : 'Member'}</div>
              </div>
            </>
          )}
        </div>

        {group.description && (
          <div style={styles.groupDesc}>{group.description}</div>
        )}

        {canJoin && (
          <>
            <button
              style={{ ...styles.joinBtn, opacity: joining ? 0.6 : 1 }}
              onClick={handleJoin}
              disabled={joining}
            >
              {joining ? 'Joining...' : '＋ Join Group'}
            </button>
            {joinError && (
              <div style={styles.joinError}>{joinError}</div>
            )}
          </>
        )}
      </div>

      {/* ── SECTION SELECTOR ── */}
      <div style={styles.selectorBar}>
        <div style={styles.selectorLabel}>View</div>
        <div style={styles.selectorWrap}>
          <select
            style={styles.selectorDropdown}
            value={activeTab}
            onChange={e => setActiveTab(e.target.value)}
          >
            <option value="chat">💬 Chat</option>
            <option value="compete">🏆 Compete</option>
            <option value="tournament">🏅 Tournament</option>
            <option value="members">👥 Members</option>
            {isAdmin && <option value="edit">⚙️ Edit Group</option>}
          </select>
          <span style={styles.selectorChevron}>▾</span>
        </div>
      </div>

      {/* ── PRIVATE GATE ── */}
      {isPrivateGate ? (
        <div style={styles.privateNotice}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🔒</div>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: '#0e1f13' }}>Private Group</div>
          <div style={{ fontSize: 13, color: '#6b7c6e' }}>You need an invitation to join this group.</div>
        </div>
      ) : (
        <>
          {/* ── CHAT TAB ── */}
          {activeTab === 'chat' && (
            <GroupChat
              key={group.id}
              group={group}
              session={session}
              myProfile={myProfile}
              isMember={isMember}
            />
          )}

          {/* ── COMPETE TAB ── */}
          {activeTab === 'compete' && (
            <GroupCompete
              groupId={groupId}
              session={session}
              members={members}
              isMember={isMember}
            />
          )}

          {/* ── TOURNAMENT TAB ── */}
          {activeTab === 'tournament' && (
            <GroupTournament
              groupId={groupId}
              session={session}
              members={members}
              isMember={isMember}
            />
          )}

          {/* ── MEMBERS TAB ── */}
          {activeTab === 'members' && (
            <div style={styles.membersTab}>
              <div style={styles.membersHeader}>
                <span>Members</span>
                <span style={styles.memberCount}>{members.length}</span>
              </div>

              {adminMembers.map(m => (
                <MemberRow key={m.user_id} member={m} isYou={m.user_id === session.user.id} />
              ))}
              {regularMembers.map(m => (
                <MemberRow key={m.user_id} member={m} isYou={m.user_id === session.user.id} />
              ))}
              {members.length === 0 && (
                <div style={styles.empty}>No members yet</div>
              )}

              {isMember && !isAdmin && (
                <div style={{ padding: '16px' }}>
                  <button style={styles.leaveBtn} onClick={handleLeave}>Leave Group</button>
                </div>
              )}
            </div>
          )}

          {/* ── EDIT TAB ── */}
          {activeTab === 'edit' && isAdmin && (
            <div style={styles.editTab}>

              {/* Group Settings */}
              <div style={styles.editSection}>
                <div style={styles.editSectionTitle}>Group Settings</div>

                <div style={styles.editField}>
                  <label style={styles.editLabel}>Group Name</label>
                  <input
                    style={styles.editInput}
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    maxLength={60}
                    placeholder="Group name"
                  />
                </div>

                <div style={styles.editField}>
                  <label style={styles.editLabel}>Description</label>
                  <textarea
                    style={{ ...styles.editInput, minHeight: 72, resize: 'vertical' }}
                    value={editDesc}
                    onChange={e => setEditDesc(e.target.value)}
                    maxLength={200}
                    placeholder="What's this group about?"
                  />
                </div>

                <div style={styles.editField}>
                  <label style={styles.editLabel}>Privacy</label>
                  <div style={styles.privacyToggle}>
                    <button
                      style={{ ...styles.privacyBtn, ...(editPrivacy === 'public' ? styles.privacyBtnActive : {}) }}
                      onClick={() => setEditPrivacy('public')}
                    >🌐 Public</button>
                    <button
                      style={{ ...styles.privacyBtn, ...(editPrivacy === 'private' ? styles.privacyBtnActive : {}) }}
                      onClick={() => setEditPrivacy('private')}
                    >🔒 Private</button>
                  </div>
                </div>

                <button
                  style={{ ...styles.saveBtn, opacity: editSaving ? 0.6 : 1 }}
                  onClick={handleSaveSettings}
                  disabled={editSaving || !editName.trim()}
                >
                  {editSaved ? '✓ Saved!' : editSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

              {/* Add Members */}
              <div style={styles.editSection}>
                <div style={styles.editSectionTitle}>Add Members</div>
                <div style={styles.memberSearchWrap}>
                  <input
                    style={styles.editInput}
                    value={memberSearch}
                    onChange={e => { setMemberSearch(e.target.value); setAddMemberError(null) }}
                    placeholder="Search by name or username..."
                    autoComplete="off"
                  />
                  {memberSearching && (
                    <div style={{ fontSize: 12, color: '#9aaa9d', marginTop: 6 }}>Searching...</div>
                  )}
                  {addMemberSuccess && (
                    <div style={styles.addSuccessBanner}>{addMemberSuccess}</div>
                  )}
                  {addMemberError && (
                    <div style={styles.addErrorBanner}>{addMemberError}</div>
                  )}
                  {memberSearchResults.map(p => (
                    <div key={p.id} style={styles.searchResultRow}>
                      <div style={styles.srAvatar}><AvatarImg profile={p} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#0e1f13' }}>
                          {p.full_name || p.username || 'Golfer'}
                        </div>
                        {p.username && (
                          <div style={{ fontSize: 11, color: '#9aaa9d' }}>@{p.username}</div>
                        )}
                      </div>
                      <button
                        style={{ ...styles.addMemberBtn, opacity: addingId === p.id ? 0.6 : 1 }}
                        onClick={() => handleAddMember(p)}
                        disabled={!!addingId}
                      >
                        {addingId === p.id ? '...' : '＋ Add'}
                      </button>
                    </div>
                  ))}
                  {memberSearch.trim() && !memberSearching && memberSearchResults.length === 0 && (
                    <div style={{ fontSize: 12, color: '#9aaa9d', marginTop: 8 }}>No users found.</div>
                  )}
                </div>
              </div>

              {/* Remove Members */}
              <div style={styles.editSection}>
                <div style={styles.editSectionTitle}>Manage Members</div>
                {removeError && <div style={styles.addErrorBanner}>{removeError}</div>}
                {members.map(m => {
                  const profile = m.profiles || {}
                  const isYou = m.user_id === session.user.id
                  return (
                    <div key={m.user_id} style={styles.manageMemberRow}>
                      <div style={styles.srAvatar}><AvatarImg profile={profile} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#0e1f13' }}>
                          @{profile.username || 'member'}{isYou && <span style={{ color: '#9aaa9d', fontWeight: 400 }}> (you)</span>}
                        </div>
                        {m.role === 'admin' && <div style={{ fontSize: 10, color: '#c8a835', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>Admin</div>}
                      </div>
                      {!isYou && (
                        <button
                          style={{ ...styles.removeBtn, opacity: removingId === m.user_id ? 0.5 : 1 }}
                          onClick={() => handleRemoveMember(m.user_id)}
                          disabled={removingId === m.user_id}
                        >
                          {removingId === m.user_id ? '...' : 'Remove'}
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>

            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── MEMBER ROW ───────────────────────────────────────────────
function MemberRow({ member, isYou }) {
  const profile = member.profiles || {}
  const displayName = profile.username
    ? `@${profile.username}`
    : profile.email
      ? profile.email.split('@')[0]
      : `Member`
  const profileHref = isYou ? '/passport' : `/passport/${member.user_id}`
  return (
    <div style={memberStyles.row}>
      <Link to={profileHref} style={memberStyles.avatarLink}>
        <div style={memberStyles.avatar}><AvatarImg profile={profile} /></div>
      </Link>
      <div style={memberStyles.info}>
        <div style={memberStyles.username}>
          <Link to={profileHref} style={memberStyles.nameLink}>
            {displayName}
          </Link>
          {isYou && <span style={memberStyles.youTag}> (you)</span>}
          {isYou && !profile.username && (
            <span style={memberStyles.setNameHint}> · Set name in Account</span>
          )}
        </div>
        {profile.handicap != null && (
          <div style={memberStyles.hcp}>HCP {profile.handicap}</div>
        )}
      </div>
      {member.role === 'admin' && (
        <div style={memberStyles.adminBadge}>Admin</div>
      )}
    </div>
  )
}

// ── STYLES ───────────────────────────────────────────────────

const styles = {
  page: { minHeight: '100vh', background: '#f4f7f5', fontFamily: "'Barlow', sans-serif", display: 'flex', flexDirection: 'column' },
  loadingScreen: {
    minHeight: '100vh', background: '#0e1f13',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
  },

  topbar: {
    background: '#0e1f13', padding: '14px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexShrink: 0
  },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 26, color: '#c8f135', letterSpacing: 2
  },
  topbarRight: { display: 'flex', alignItems: 'center', gap: 8 },
  topbarIcon: { fontSize: 18 },
  topbarTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 18, color: 'white',
    letterSpacing: 0.5, maxWidth: 200,
    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis'
  },

  groupHeader: {
    background: '#0e1f13', padding: '0 20px 16px',
    textAlign: 'center', flexShrink: 0
  },
  groupDesc: {
    fontSize: 13, color: 'rgba(255,255,255,0.45)',
    marginBottom: 12, lineHeight: 1.4
  },
  headerStats: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    paddingBottom: 14
  },
  headerStat: { textAlign: 'center', padding: '0 18px' },
  headerStatNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 20, color: '#c8f135', lineHeight: 1
  },
  headerStatLabel: {
    fontSize: 9, color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase', letterSpacing: 1, marginTop: 3
  },
  headerStatDivider: { width: 1, height: 28, background: 'rgba(255,255,255,0.1)' },

  joinBtn: {
    background: '#c8f135', color: '#0e1f13', border: 'none',
    borderRadius: 12, padding: '12px 28px', fontSize: 15,
    fontWeight: 900, cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  },
  joinError: {
    marginTop: 10, fontSize: 12, color: '#f87171',
    background: 'rgba(232,64,64,0.1)', borderRadius: 8,
    padding: '7px 14px'
  },

  // ── Section selector ─────────────────────────────────
  selectorBar: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 16px', background: 'white',
    borderBottom: '1.5px solid #f0f4f1', flexShrink: 0
  },
  selectorLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: 1.2,
    textTransform: 'uppercase', color: '#9aaa9d', flexShrink: 0
  },
  selectorWrap: {
    position: 'relative', flex: 1
  },
  selectorDropdown: {
    width: '100%', appearance: 'none', WebkitAppearance: 'none',
    background: '#f4f7f5', border: '1.5px solid #e2ebe4',
    borderRadius: 10, padding: '9px 36px 9px 12px',
    fontSize: 14, fontWeight: 700, color: '#0e1f13',
    cursor: 'pointer', fontFamily: "'Barlow', sans-serif",
    outline: 'none'
  },
  selectorChevron: {
    position: 'absolute', right: 12, top: '50%',
    transform: 'translateY(-50%)', fontSize: 14,
    color: '#6b7c6e', pointerEvents: 'none'
  },

  // ── Members tab ───────────────────────────────────────
  membersTab: { background: 'white', flex: 1 },
  membersHeader: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '14px 18px 12px',
    borderBottom: '1px solid #f0f4f1',
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 14, color: '#0e1f13',
    letterSpacing: 0.8, textTransform: 'uppercase'
  },
  memberCount: {
    background: '#e8f5ed', color: '#1a7a3c', borderRadius: 20,
    padding: '2px 8px', fontSize: 12, fontWeight: 700,
    fontFamily: "'Barlow', sans-serif", textTransform: 'none', letterSpacing: 0
  },
  empty: { padding: 24, textAlign: 'center', color: '#6b7c6e', fontSize: 13 },

  leaveBtn: {
    width: '100%', background: 'none',
    border: '1.5px solid #e2ebe4', borderRadius: 12,
    padding: '12px', fontSize: 13, fontWeight: 700,
    color: '#c0392b', cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },

  privateNotice: {
    background: 'white', margin: 16, borderRadius: 18,
    padding: '40px 20px', textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },

  // ── Edit tab ──────────────────────────────────────────
  editTab: { padding: '16px', display: 'flex', flexDirection: 'column', gap: 14 },
  editSection: {
    background: 'white', borderRadius: 16,
    padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  editSectionTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 13, color: '#0e1f13',
    letterSpacing: 1, textTransform: 'uppercase',
    marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid #f0f4f1'
  },
  editField: { marginBottom: 14 },
  editLabel: {
    display: 'block', fontSize: 11, fontWeight: 700,
    color: '#6b7c6e', textTransform: 'uppercase',
    letterSpacing: 0.8, marginBottom: 6
  },
  editInput: {
    width: '100%', boxSizing: 'border-box',
    border: '1.5px solid #e2ebe4', borderRadius: 12,
    padding: '11px 14px', fontSize: 14, color: '#0e1f13',
    fontFamily: "'Barlow', sans-serif", outline: 'none',
    background: '#f8faf8'
  },
  privacyToggle: { display: 'flex', gap: 8 },
  privacyBtn: {
    flex: 1, background: '#f4f7f5', border: '1.5px solid #e2ebe4',
    borderRadius: 10, padding: '10px', fontSize: 13,
    fontWeight: 700, color: '#6b7c6e', cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif"
  },
  privacyBtnActive: {
    background: '#e8f5ed', border: '1.5px solid #1a7a3c', color: '#1a7a3c'
  },
  saveBtn: {
    width: '100%', background: '#c8f135', color: '#0e1f13',
    border: 'none', borderRadius: 12, padding: '13px',
    fontSize: 14, fontWeight: 900, cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1
  },
  memberSearchWrap: { display: 'flex', flexDirection: 'column', gap: 6 },
  searchResultRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 0', borderBottom: '1px solid #f0f4f1'
  },
  manageMemberRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 0', borderBottom: '1px solid #f0f4f1'
  },
  srAvatar: {
    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
  },
  addMemberBtn: {
    background: '#0e1f13', color: '#c8f135', border: 'none',
    borderRadius: 8, padding: '7px 14px', fontSize: 13,
    fontWeight: 800, cursor: 'pointer', flexShrink: 0,
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.3
  },
  addSuccessBanner: {
    background: '#e8f5ed', color: '#1a7a3c', borderRadius: 10,
    padding: '8px 12px', fontSize: 13, fontWeight: 700
  },
  addErrorBanner: {
    background: 'rgba(232,64,64,0.08)', color: '#c0392b',
    borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 600,
    border: '1px solid rgba(192,57,43,0.2)'
  },
  removeBtn: {
    background: '#fde8e8', color: '#c0392b', border: 'none',
    borderRadius: 8, padding: '6px 12px', fontSize: 12,
    fontWeight: 700, cursor: 'pointer', flexShrink: 0,
    fontFamily: "'Barlow', sans-serif"
  }
}

const memberStyles = {
  row: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 18px', borderBottom: '1px solid #f0f4f1'
  },
  avatarLink: { textDecoration: 'none', flexShrink: 0 },
  avatar: {
    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20
  },
  info: { flex: 1 },
  username: { fontWeight: 700, fontSize: 14, color: '#0e1f13' },
  nameLink: { textDecoration: 'none', color: '#0e1f13', fontWeight: 700 },
  youTag: { fontWeight: 400, color: '#6b7c6e', fontSize: 12 },
  hcp: { fontSize: 11, color: '#6b7c6e', marginTop: 2 },
  setNameHint: { fontSize: 10, color: '#c8a835', fontWeight: 600 },
  adminBadge: {
    background: 'rgba(200,168,53,0.12)', color: '#c8a835',
    borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700
  }
}
